import {
  BLOCKS_PER_SECTION, CHUNK_X, CHUNK_Z, NIBBLES_PER_SECTION, SECTION_COUNT,
  SECTION_Y, WORLD_MAX_Y, WORLD_MIN_Y, chunkKey, chunkKeyX, chunkKeyZ,
  sectionIndex, type DimensionId,
} from '../shared/constants.ts';
import { AIR_STATE, lightEmission, lightFilter } from '../shared/registry/blocks.ts';
import { Chunk, Section } from '../shared/world/chunk.ts';
import { JOB_W, PAD, type MeshJob } from './mesher/types.ts';

/**
 * The client's copy of the world.
 *
 * Blocks and light arrive from the server; the client never generates terrain
 * or propagates light itself. Its job is to keep the arrays around for the
 * mesher, physics prediction and raycasting, and to track which chunks need
 * remeshing.
 */
export class ClientWorld {
  readonly chunks = new Map<number, Chunk>();
  dimension: DimensionId = 'overworld';
  /** Chunk keys that need a new mesh. */
  readonly dirty = new Set<number>();
  /** Bumped per chunk on every change so stale mesh results can be discarded. */
  readonly revisions = new Map<number, number>();

  getChunk(cx: number, cz: number): Chunk | undefined {
    return this.chunks.get(chunkKey(cx, cz));
  }

  hasChunk(cx: number, cz: number): boolean {
    return this.chunks.has(chunkKey(cx, cz));
  }

  getState(x: number, y: number, z: number): number {
    if (y < WORLD_MIN_Y || y >= WORLD_MAX_Y) return AIR_STATE;
    const chunk = this.chunks.get(chunkKey(x >> 4, z >> 4));
    if (!chunk) return AIR_STATE;
    return chunk.getState(x & 15, y, z & 15);
  }

  getSkyLight(x: number, y: number, z: number): number {
    const chunk = this.chunks.get(chunkKey(x >> 4, z >> 4));
    if (!chunk) return 15;
    return chunk.getSkyLight(x & 15, y, z & 15);
  }

  getBlockLight(x: number, y: number, z: number): number {
    const chunk = this.chunks.get(chunkKey(x >> 4, z >> 4));
    if (!chunk) return 0;
    return chunk.getBlockLight(x & 15, y, z & 15);
  }

  getBiome(x: number, z: number): number {
    const chunk = this.chunks.get(chunkKey(x >> 4, z >> 4));
    return chunk ? chunk.getBiome(x & 15, z & 15) : 0;
  }

  setChunk(chunk: Chunk): void {
    const key = chunkKey(chunk.cx, chunk.cz);
    this.chunks.set(key, chunk);
    this.revisions.set(key, (this.revisions.get(key) ?? 0) + 1);
    this.markDirtyWithNeighbors(chunk.cx, chunk.cz);
  }

  removeChunk(cx: number, cz: number): void {
    const key = chunkKey(cx, cz);
    this.chunks.delete(key);
    this.dirty.delete(key);
    this.revisions.delete(key);
  }

  clear(): void {
    this.chunks.clear();
    this.dirty.clear();
    this.revisions.clear();
  }

  /**
   * Apply a single block change. Marks the containing chunk dirty, plus any
   * neighbour whose border geometry could be affected.
   */
  setBlock(x: number, y: number, z: number, state: number): void {
    const cx = x >> 4;
    const cz = z >> 4;
    const chunk = this.chunks.get(chunkKey(cx, cz));
    if (!chunk) return;
    chunk.setState(x & 15, y, z & 15, state);
    this.markDirty(cx, cz);

    const lx = x & 15;
    const lz = z & 15;
    if (lx === 0) this.markDirty(cx - 1, cz);
    if (lx === 15) this.markDirty(cx + 1, cz);
    if (lz === 0) this.markDirty(cx, cz - 1);
    if (lz === 15) this.markDirty(cx, cz + 1);
    if (lx === 0 && lz === 0) this.markDirty(cx - 1, cz - 1);
    if (lx === 15 && lz === 0) this.markDirty(cx + 1, cz - 1);
    if (lx === 0 && lz === 15) this.markDirty(cx - 1, cz + 1);
    if (lx === 15 && lz === 15) this.markDirty(cx + 1, cz + 1);
  }

  markDirty(cx: number, cz: number): void {
    const key = chunkKey(cx, cz);
    if (!this.chunks.has(key)) return;
    this.dirty.add(key);
    this.revisions.set(key, (this.revisions.get(key) ?? 0) + 1);
  }

  markDirtyWithNeighbors(cx: number, cz: number): void {
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) this.markDirty(cx + dx, cz + dz);
    }
  }

  /**
   * Build a mesh job for a chunk: a padded copy of its blocks and light, with
   * a one-block border from the neighbours, clipped to the vertical span that
   * actually contains geometry.
   */
  buildJob(cx: number, cz: number, id: number): MeshJob | null {
    const chunk = this.chunks.get(chunkKey(cx, cz));
    if (!chunk) return null;

    // Vertical span: any section that is non-empty in this chunk or a neighbour.
    let minSection = SECTION_COUNT;
    let maxSection = -1;
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const c = this.chunks.get(chunkKey(cx + dx, cz + dz));
        if (!c) continue;
        for (let sy = 0; sy < SECTION_COUNT; sy++) {
          const s = c.sections[sy];
          if (!s || s.isEmpty()) continue;
          if (sy < minSection) minSection = sy;
          if (sy > maxSection) maxSection = sy;
        }
      }
    }
    if (maxSection < 0) return null;
    // One section of slack above and below so border faces are lit correctly.
    minSection = Math.max(0, minSection - 1);
    maxSection = Math.min(SECTION_COUNT - 1, maxSection + 1);

    const minY = WORLD_MIN_Y + minSection * SECTION_Y;
    const height = (maxSection - minSection + 1) * SECTION_Y;
    const size = JOB_W * height * JOB_W;
    const blocks = new Uint16Array(size);
    const skyLight = new Uint8Array(size);
    const blockLight = new Uint8Array(size);
    const biomes = new Uint8Array(JOB_W * JOB_W);

    const baseX = cx * CHUNK_X;
    const baseZ = cz * CHUNK_Z;

    for (let pz = 0; pz < JOB_W; pz++) {
      const wz = baseZ + pz - PAD;
      for (let px = 0; px < JOB_W; px++) {
        const wx = baseX + px - PAD;
        const c = this.chunks.get(chunkKey(wx >> 4, wz >> 4));
        biomes[pz * JOB_W + px] = c ? c.getBiome(wx & 15, wz & 15) : 0;
        if (!c) {
          // Missing neighbour: treat as fully lit air so the seam is not black.
          for (let y = 0; y < height; y++) {
            skyLight[(y * JOB_W + pz) * JOB_W + px] = 15;
          }
          continue;
        }
        const lx = wx & 15;
        const lz = wz & 15;
        for (let sy = minSection; sy <= maxSection; sy++) {
          const section = c.sections[sy];
          const yBase = (sy - minSection) * SECTION_Y;
          if (!section) {
            const lit = c.getHeight(lx, lz) <= WORLD_MIN_Y + sy * SECTION_Y ? 15 : 0;
            for (let ly = 0; ly < SECTION_Y; ly++) {
              skyLight[((yBase + ly) * JOB_W + pz) * JOB_W + px] = lit;
            }
            continue;
          }
          for (let ly = 0; ly < SECTION_Y; ly++) {
            const si = sectionIndex(lx, ly, lz);
            const di = ((yBase + ly) * JOB_W + pz) * JOB_W + px;
            blocks[di] = section.blocks[si]!;
            skyLight[di] = section.getSky(si);
            blockLight[di] = section.getBlockLight(si);
          }
        }
      }
    }

    return {
      id,
      cx,
      cz,
      minY,
      height,
      blocks,
      skyLight,
      blockLight,
      biomes,
      revision: this.revisions.get(chunkKey(cx, cz)) ?? 0,
    };
  }
}

/** Decode the section-light payload the server appends for empty sections. */
export function applyEmptySectionLight(chunk: Chunk, sy: number, mode: number): void {
  if (mode === 1) {
    // Fully skylit air above the terrain: no section needs allocating, the
    // chunk's heightmap already implies light 15.
    return;
  }
  if (mode === 0) return;
}
